import { useDispatch, useSelector } from "react-redux";
import { useRef, useState, useEffect } from "react";
import { NavLink } from "react-router-dom";
import { useIntl } from "react-intl";

//Components
import InputDate from 'Components/InputDate';
import moment from 'moment';
import cx from 'classnames';
import {
    Grid,
    Typography,
    Container,
    Paper,
    Divider,
    Icon
} from "@mui/material";
import { NotificationManager } from "Components/Notifications";
import DataViewInfiniteScroll from 'Components/DataViewInfiniteScroll';
import Filters from 'Components/Filters';

import AdvancedFilters from 'Modules/maintenanceWarningEvents/views/components/AdvancedFilters';

//Helpers
import { getDateFormatted, getBadget, getMinDateLimitBehaviour } from 'Core/data/Helpers';
import { optionsTimeFrame } from 'Core/defaultValues';
import { getTimeFrame } from 'Utils/helpers';

//Actions
import { maintenanceWarningsEventsOpenModal } from 'Redux/actions';

//Views
import Details from 'Modules/maintenanceWarningEvents/views/components/details';

//Styles
import './styles.scss';

const RANGE_MODE_ROWS = 50;
const FORCE_CURRENT_WEEK_RANGE = false; // true = always use current week dates on mount, ignoring saved preferences

const MaintenanceWarningEvents = (props) => {
    const { tabMode, height, companyId, device } = props
    const { messages } = useIntl();
    const dispatch = useDispatch();
    const tableRef = useRef();

    //Local states
    const [initialLoad, setInitialLoad] = useState(true)
    const [total, setTotal] = useState(0);
    const rawDataFilters = useSelector(state => state.rawDataTabRedux.filters);

    //Local States
    const initialDate = moment().day(0).startOf('day').toDate()
    const tempStartDate = new Date(initialDate.toString()); 
    const [startDate, setStartDate] = useState(FORCE_CURRENT_WEEK_RANGE ? tempStartDate : (rawDataFilters?.startDate ||tempStartDate));
    const [endDate, setEndDate] = useState(FORCE_CURRENT_WEEK_RANGE ? new Date() : (rawDataFilters?.endDate || new Date()));
    const [beforeRangeDate, setBeforeRangeDate] = useState({
        start: startDate,
        end: endDate
    })

    const [last, setLast] = useState(rawDataFilters?.last ||{ id: '20', name: '20' });
    const [type, setType] = useState(rawDataFilters?.type || 'last');

    const [manufacturer, setManufacturer] = useState(null);
    const [deviceModel, setDeviceModel] = useState(null);
    const [openPanel, setOpenPanel] = useState(false);
    const [status, setStatus] = useState(null);
    const [account, setAccount] = useState(null);

    const onChangeStartEndDate = ({ start, end }) => {
        const newStart = start && new Date(start);
        const newEnd = end && new Date(end);
        setStartDate(newStart);
        setEndDate(newEnd);
    }

    const onChange = (value) => {
        setInitialLoad(false)
        const { start, end } = value;
        onChangeStartEndDate({
            start,
            end
        })
    };

    const applyFilters = (paramType) => {
        const currentType = paramType ?? type;
        const DEFAULT_ROWS = 50;

        if (currentType !== "last" && (!startDate || !endDate)) {
            NotificationManager.warning(
                messages['maintenance_warnings_tab_date_range'],
                messages['maintenance_warnings_tab_title'],
                5000,
            );

            return
        }

        // Build conditions fresh - preserve only search tags
        let currentConditions = tableRef?.current?.conditions || [];
        let searchTags = currentConditions.find((item) => item?.tags);
        let newConditions = searchTags ? [searchTags] : [];

        // Condition 1: ID filter (companyId or unitId or none)
        if (companyId) {
            newConditions.push({
                fields: ["unit_companyId"], terms: [companyId], exact: true,
                conditionOperator: "AND"
            });
        } else if (device?.id) {
            newConditions.push({
                fields: ["unitId"], terms: [device?.id], exact: true,
                conditionOperator: "AND"
            });
        }

        // Condition 2: Range filter (SEPARATE - only in range mode)
        if (currentType !== "last") {
            newConditions.push({
                range: {
                    fields: ["unitTime"],
                    GTE: moment(moment(startDate).format("YYYY-MM-DD") + " 00:00:00").utc().format("YYYY-MM-DDTHH:mm:ss.sss") + "Z",
                    LTE: moment(moment(endDate).format("YYYY-MM-DD") + " 23:59:00").utc().format("YYYY-MM-DDTHH:mm:ss.sss") + "Z"
                },
                conditionOperator: "AND"
            });
        }

        tableRef?.current?.getData({ newRows: currentType === 'last' ? parseInt(last.id, 10) : DEFAULT_ROWS, newSort: currentType === 'last' ? { field: "unitTime", order: "DESC" } : undefined, newConditions });

    }

    const handleChangeColumns = (items) => {
        if (companyId) setCompanyColumns(items)
        else if (device?.id) setDeviceColumns(items)
        else setMainColumns(items)
    }

    const handleChangeConditions = (tags, conditions, total, newColumns, rows, sort, key, fromPreferences, restoreOnly) => {
        const DEFAULT_ROWS = 50;
        
        let incomingConditions = [];
        let currentType = type
        let currentStartDate = startDate
        let currentEndDate = endDate

        let currentTags = []
        if (!conditions && tags) {
            incomingConditions = tableRef?.current?.conditions || [];
            currentTags = tags;
        } else {
            incomingConditions = conditions || [];
            currentTags = incomingConditions?.filter((x) => x?.tags)[0]?.tags || [];
            if (currentTags && currentTags.length > 0) tableRef?.current?.setTags(currentTags);
        }

        // Detect range mode from incoming conditions (preferences restore)
        let currentDateType = incomingConditions.find((item) => item?.range?.fields);
        if (currentDateType) {
            // Extract dates from range condition
            const tz = device?.timezone || moment.tz.guess();
            const restoredStart = new Date(moment.utc(currentDateType.range.GTE).tz(tz).format("YYYY-MM-DD") + "T00:00:00");
            const restoredEnd = new Date(moment.utc(currentDateType.range.LTE).tz(tz).format("YYYY-MM-DD") + "T00:00:00");
            if (!FORCE_CURRENT_WEEK_RANGE) {
                setStartDate(restoredStart);
                setEndDate(restoredEnd);
                currentStartDate = restoredStart;
                currentEndDate = restoredEnd;
            }
            setType('range');
            currentType = 'range';
        } else {
            setType('last');
            currentType = 'last';
        }

        // Enforce minimum rows in range mode for consistency
        const effectiveRows = currentType === 'last' 
            ? rows 
            : Math.max(rows || DEFAULT_ROWS, DEFAULT_ROWS);

        if (effectiveRows)
            setLast({ id: `${effectiveRows}`, name: `${effectiveRows}` })

        if (!tabMode) {
            let validateAccount = incomingConditions && incomingConditions?.find((x) => x?.fields && x?.fields[0] === "unit_companyId");
            validateAccount && validateAccount?.account && setAccount(validateAccount?.account);

            let validateManufacturer = incomingConditions && incomingConditions?.find((x) => x?.fields && x?.fields[0] === "unit_deviceModel_manufacturerId");
            validateManufacturer && setManufacturer(validateManufacturer.terms[0]);

            let validateDeviceModel = incomingConditions && incomingConditions?.find((x) => x?.fields && x?.fields[0] === "unit_deviceModel_id");
            validateDeviceModel && setDeviceModel(validateDeviceModel.terms[0]);

            let validateStatus = incomingConditions && incomingConditions?.find((x) => x?.fields && x?.fields[0] === "unit_status");
            validateStatus && setStatus(validateStatus.terms[0]);
        }

        // Build conditions fresh - filter out existing search conditions (same pattern as Events tab)
        // This prevents duplication where old search condition was preserved AND new one was pushed
        let newConditions = [];

        // Add search tags by label if any - creates SINGLE combined condition with all terms
        let tagsByLabel = currentTags.filter(item => typeof item.id === "string")
        if (tagsByLabel.length > 0) {
            let currentLabels = tagsByLabel.map((item) => item.name)
            if (currentLabels.length > 0) {
                newConditions.push({ fields: device?.id ? ["warning"] : ["warning", "unit_label", "unit_esn"], terms: currentLabels, exact: false, tags: currentTags, conditionOperator: "OR" })
            }
        }

        // Condition 1: ID filter (companyId or unitId or none)
        if (companyId) {
            newConditions.push({
                fields: ["unit_companyId"], terms: [companyId], exact: true,
                conditionOperator: "AND"
            });
        } else if (device?.id) {
            newConditions.push({
                fields: ["unitId"], terms: [device?.id], exact: true,
                conditionOperator: "AND"
            });
        }

        // Condition 2: Range filter (SEPARATE - only in range mode)
        if (currentType !== 'last') {
            newConditions.push({
                range: {
                    fields: ["unitTime"],
                    GTE: moment(moment(currentStartDate).format("YYYY-MM-DD") + " 00:00:00").utc().format("YYYY-MM-DDTHH:mm:ss.sss") + "Z",
                    LTE: moment(moment(currentEndDate).format("YYYY-MM-DD") + " 23:59:00").utc().format("YYYY-MM-DDTHH:mm:ss.sss") + "Z"
                },
                conditionOperator: "AND"
            });
        }

        // restoreOnly: data already persisted in redux, just sync UI states — skip getData
        if (restoreOnly) return;

        // Search-only changes (!conditions && tags) should NOT pass newSort to preserve user's current sort
        if (!conditions && tags) tableRef?.current?.getData({ newRows: effectiveRows, newConditions, newColumns });
        else tableRef?.current?.getData({ newRows: effectiveRows, newSort: sort || (currentType === 'last' ? { field: "unitTime", order: "DESC" } : undefined), newConditions });
    }

    const renderExtraButton = () => {
        return (
            <div style={{ /* flex: '1 1 auto',  */display: 'flex', justifyContent: 'flex-end' }}>
                    <Filters
                        startDate={startDate}
                        endDate={endDate}
                        setStartDate={setStartDate}
                        setEndDate={setEndDate}
                        setBeforeRangeDate={setBeforeRangeDate}
                        beforeRangeDate={beforeRangeDate}
                        onChangeFilters={() => applyFilters()}
                        last={last}
                        setLast={setLast}
                        type={type}
                        setType={(value) => {
                            setType(value);
                            if (value === 'range') {
                                tableRef?.current?.setRows(RANGE_MODE_ROWS);
                            }
                            applyFilters(value)
                        }}
                    />
            </div>
        )
    }

    const [companyColumns, setCompanyColumns] = useState([
        {
            key: 'view',
            show: true,
            locked: true,
            title: "",
            width: 40,
            icon: true,
            render: (item) => {
                return (
                    <Icon style={{ cursor: "pointer", color: '#1D2734' }} className="toolbar-list-icon fs-20" onClick={() => {
                        dispatch(maintenanceWarningsEventsOpenModal(item.details, item?.unit?.timezone))
                    }}>
                        remove_red_eye
                    </Icon>
                )
            }
        },
        {
            key: "unit_esn",
            title: messages['maintenancewarningevent_main_esn'],
            width: 200,
            show: true,
            sort: true,
            locked: true,
            resize: true,
            export: true,
            functionString: 'return row.unit.esn',
            code: "maintenancewarningevent_main_esn",
            render: (item) => {
                return (
                    <NavLink to={`/app/devices/overview/${item.unit.id}`} style={{ color: 'black' }}>
                        <span>{item.unit.esn}</span>
                    </NavLink>
                )
            }
        },
        {
            key: "unit_label",
            title: messages['maintenancewarningevent_main_label'],
            width: 250,
            show: true,
            sort: true,
            locked: false,
            resize: true,
            export: true,
            functionString: 'return row.unit.label',
            code: "maintenancewarningevent_main_label",
            render: (item) => {
                return (
                    <NavLink to={`/app/devices/overview/${item.unit.id}`} style={{ color: 'black' }}>
                        <span> {item.unit.label}</span>
                    </NavLink>
                )
            }
        },
        {
            key: "unit_linkedMode",
            title: messages['maintenancewarningevent_main_linkedMode'],
            width: 150,
            show: true,
            sort: true,
            locked: false,
            resize: true,
            export: true,
            functionString: 'return `${row?.unit?.deviceModel?.manufacturer?.name === "VidFleet"? row?.unit?.linkedMode ? "Paired Mode" : "Camera as GPS" : ""}`',
            code: "maintenancewarningevent_main_linkedMode",
            render: (item) => {
                return (
                    <span>
                        {
                            (item?.unit?.deviceModel?.manufacturer?.name?.toUpperCase() === "Vidfleet".toUpperCase() || item?.unit?.deviceModel?.manufacturer?.name?.toUpperCase() === "SmartWitness".toUpperCase()) &&
                            getBadget(item?.unit?.linkedMode ? messages["devices_import_camera_paired_mode"] : messages["devices_import_camera_gps"], item?.unit?.linkedMode)
                        }
                    </span>
                )
            }
        },
        {
            key: "unit_linkedUnit_label",
            title: messages['maintenancewarningevent_main_linkedUnitLabel'],
            width: 250,
            show: true,
            sort: true,
            locked: false,
            resize: true,
            export: true,
            functionString: 'return row?.unit?.linkedUnit?.label',
            code: "maintenancewarningevent_main_linkedUnitLabel",
            render: (item) => {
                return (
                    <div style={{ display: 'flex', width: 180, justifyContent: 'space-between' }}>
                        {item?.unit?.linkedUnit?.label}
                    </div>
                )
            }
        },
        {
            key: "unitTime",
            field: "unitTime",
            title: messages['maintenancewarningevent_main_unitTime'],
            width: 250,
            show: true,
            sort: true,
            locked: false,
            resize: true,
            export: true,
            functionString: `
            function formatDate(date,tz) {
                let dateToformat = moment(date)
                let formatted = moment(dateToformat).tz(tz).format("YYYY/MM/DD HH:mm:ss")
                return formatted;
              }

              return row?.unitTime ? formatDate(row?.unitTime,row?.unit?.timezone) : ''
            `,
            code: "maintenancewarningevent_main_unitTime",
            render: (item) => {
                return <span>{item?.unitTime && getDateFormatted(item?.unitTime, "date", "datetime", null, item?.unit?.timezone)}</span>
            }
        },
        {
            key: "warning",
            field: "warning",
            title: messages['maintenancewarningevent_main_warning'],
            width: 250,
            show: true,
            sort: true,
            locked: false,
            resize: true,
            export: true,
            functionString: 'return row?.warning',
            code: "maintenancewarningevent_main_warning",
            render: (item) => {
                return (
                    <div style={{ display: 'flex', width: 180, justifyContent: 'space-between' }}>
                        {item.warning}
                    </div>
                )
            }
        },
        {
            key: "value",
            field: "value",
            title: messages['maintenancewarningevent_main_value'],
            width: 150,
            show: true,
            sort: true,
            locked: false,
            resize: true,
            export: true,
            functionString: 'return row?.value',
            code: "maintenancewarningevent_main_value",
            render: (item) => {
                return (
                    <div style={{ display: 'flex', width: 180, justifyContent: 'space-between' }}>
                        {item.value}
                    </div>
                )
            }
        }

    ])

    const [deviceColumns, setDeviceColumns] = useState([
        {
            key: 'view',
            show: true,
            locked: true,
            title: "",
            width: 40,
            icon: true,
            render: (item) => {
                return (
                    <Icon style={{ cursor: "pointer", color: '#1D2734' }} className="toolbar-list-icon fs-20" onClick={() => {
                        dispatch(maintenanceWarningsEventsOpenModal(item.details, item?.unit?.timezone))
                    }}>
                        remove_red_eye
                    </Icon>
                )
            }
        },
        {
            key: "unitTime",
            field: "unitTime",
            title: messages['maintenancewarningevent_main_unitTime'],
            width: 250,
            show: true,
            sort: true,
            locked: false,
            resize: true,
            export: true,
            functionString: `
            function formatDate(date,tz) {
                let dateToformat = moment(date)
                let formatted = moment(dateToformat).tz(tz).format("YYYY/MM/DD HH:mm:ss")
                return formatted;
              }

              return row?.unitTime ? formatDate(row?.unitTime,row?.unit?.timezone) : ''
            `,
            code: "maintenancewarningevent_main_unitTime",
            render: (item) => {
                return <span>{item?.unitTime && getDateFormatted(item?.unitTime, "date", "datetime", null, item?.unit?.timezone)}</span>
            }
        },
        {
            key: "warning",
            field: "warning",
            title: messages['maintenancewarningevent_main_warning'],
            /*  width: 250, */
            show: true,
            sort: true,
            locked: false,
            resize: true,
            export: true,
            functionString: 'return row?.warning',
            code: "maintenancewarningevent_main_warning",
            render: (item) => {
                return (
                    <div style={{ display: 'flex', width: 180, justifyContent: 'space-between' }}>
                        {item.warning}
                    </div>
                )
            }
        },
        {
            key: "value",
            field: "value",
            title: messages['maintenancewarningevent_main_value'],
            width: 150,
            show: true,
            sort: true,
            locked: false,
            resize: true,
            export: true,
            functionString: 'return row?.value',
            code: "maintenancewarningevent_main_value",
            render: (item) => {
                return (
                    <div style={{ display: 'flex', width: 180, justifyContent: 'space-between' }}>
                        {item.value}
                    </div>
                )
            }
        }
    ])

    const [mainColumns, setMainColumns] = useState([
        {
            key: 'view',
            show: true,
            locked: true,
            title: "",
            width: 40,
            icon: true,
            render: (item) => {
                return (
                    <Icon style={{ cursor: "pointer", color: '#1D2734' }} className="toolbar-list-icon fs-20" onClick={() => {
                        dispatch(maintenanceWarningsEventsOpenModal(item.details, item?.unit?.timezone))
                    }}>
                        remove_red_eye
                    </Icon>
                )
            }
        },
        {
            key: "unit_company_name",
            title: messages['maintenancewarningevent_main_company'],
            width: 200,
            show: true,
            sort: true,
            locked: true,
            resize: true,
            export: true,
            functionString: 'return row?.unit?.company?.name',
            code: "maintenancewarningevent_main_company",
            render: (item) => {
                return (
                    <NavLink to={`/app/accounts/overview/${item.unit?.company?.id}`} style={{ color: 'black' }}>
                        <span>{item?.unit?.company?.name}</span>
                    </NavLink>
                )
            }
        },
        {
            key: "unit_esn",
            title: messages['maintenancewarningevent_main_esn'],
            width: 200,
            show: true,
            sort: true,
            locked: false,
            resize: true,
            export: true,
            functionString: 'return row.unit.esn',
            code: "maintenancewarningevent_main_esn",
            render: (item) => {
                return (
                    <NavLink to={`/app/devices/overview/${item.unit.id}`} style={{ color: 'black' }}>
                        <span>{item.unit.esn}</span>
                    </NavLink>
                )
            }
        },
        {
            key: "unit_label",
            title: messages['maintenancewarningevent_main_label'],
            width: 250,
            show: true,
            sort: true,
            locked: false,
            resize: true,
            export: true,
            functionString: 'return row.unit.label',
            code: "maintenancewarningevent_main_label",
            render: (item) => {
                return (
                    <NavLink to={`/app/devices/overview/${item.unit.id}`} style={{ color: 'black' }}>
                        <span> {item.unit.label}</span>
                    </NavLink>
                )
            }
        },
        {
            key: "unit_linkedMode",
            title: messages['maintenancewarningevent_main_linkedMode'],
            width: 150,
            show: true,
            sort: true,
            locked: false,
            resize: true,
            export: true,
            functionString: 'return `${row?.unit?.deviceModel?.manufacturer?.name === "VidFleet"? row?.unit?.linkedMode ? "Paired Mode" : "Camera as GPS" : ""}`',
            code: "maintenancewarningevent_main_linkedMode",
            render: (item) => {
                return (
                    <span>
                        {
                            (item?.unit?.deviceModel?.manufacturer?.name?.toUpperCase() === "Vidfleet".toUpperCase() || item?.unit?.deviceModel?.manufacturer?.name?.toUpperCase() === "SmartWitness".toUpperCase()) &&
                            getBadget(item?.unit?.linkedMode ? messages["devices_import_camera_paired_mode"] : messages["devices_import_camera_gps"], item?.unit?.linkedMode)
                        }
                    </span>
                )
            }
        },
        {
            key: "unit_linkedUnit_label",
            title: messages['maintenancewarningevent_main_linkedUnitLabel'],
            width: 250,
            show: true,
            sort: true,
            locked: false,
            resize: true,
            export: true,
            functionString: 'return row?.unit?.linkedUnit?.label',
            code: "maintenancewarningevent_main_linkedUnitLabel",
            render: (item) => {
                return (
                    <div style={{ display: 'flex', width: 180, justifyContent: 'space-between' }}>
                        {item?.unit?.linkedUnit?.label}
                    </div>
                )
            }
        },
        {
            key: "unitTime",
            field: "unitTime",
            title: messages['maintenancewarningevent_main_unitTime'],
            width: 250,
            show: true,
            sort: true,
            locked: false,
            resize: true,
            export: true,
            functionString: `
            function formatDate(date,tz) {
                let dateToformat = moment(date)
                let formatted = moment(dateToformat).tz(tz).format("YYYY/MM/DD HH:mm:ss")
                return formatted;
              }

              return row?.unitTime ? formatDate(row?.unitTime,row?.unit?.timezone) : ''
            `,
            code: "maintenancewarningevent_main_unitTime",
            render: (item) => {
                return <span>{item?.unitTime && getDateFormatted(item?.unitTime, "date", "datetime", null, item?.unit?.timezone)}</span>
            }
        },
        {
            key: "warning",
            field: "warning",
            title: messages['maintenancewarningevent_main_warning'],
            width: 250,
            show: true,
            sort: true,
            locked: false,
            resize: true,
            export: true,
            functionString: 'return row?.warning',
            code: "maintenancewarningevent_main_warning",
            render: (item) => {
                return (
                    <div style={{ display: 'flex', width: 180, justifyContent: 'space-between' }}>
                        {item.warning}
                    </div>
                )
            }
        },
        {
            key: "value",
            field: "value",
            title: messages['maintenancewarningevent_main_value'],
            width: 150,
            show: true,
            sort: true,
            locked: false,
            resize: true,
            export: true,
            functionString: 'return row?.value',
            code: "maintenancewarningevent_main_value",
            render: (item) => {
                return (
                    <div style={{ display: 'flex', width: 180, justifyContent: 'space-between' }}>
                        {item.value}
                    </div>
                )
            }
        }

    ])

    const { openModal } = useSelector(state => state.maintenanceWarningEventsDetailsRedux);

    useEffect(() => {
        if (!initialLoad) {
            if (startDate && endDate) {
                let currentConditions = tableRef?.current?.conditions;

                let newConditions = (!companyId || !device?.id) ? currentConditions.filter((item) => !item?.range && !item?.type) : currentConditions.filter((item) => item.fields && item.fields[0] !== 'unit_companyId' && item.fields[0] !== 'unitId' && item?.fields[0] !== 'warning' && !item?.type);
                if (companyId) {
                    newConditions.push({
                        fields: ["unit_companyId"], terms: [companyId], exact: true,
                        range: {
                            fields: ["unitTime"],
                            GTE: moment(moment(startDate).format("YYYY-MM-DD") + " 00:00:00").utc().format("YYYY-MM-DDTHH:mm:ss.sss") + "Z",
                            LTE: moment(moment(endDate).format("YYYY-MM-DD") + " 23:59:00").utc().format("YYYY-MM-DDTHH:mm:ss.sss") + "Z"
                        },
                        conditionOperator: "AND"
                    });
                } else if (device?.id) {
                    newConditions.push({
                        fields: ["unitId"], terms: [device?.id], exact: true,
                        range: {
                            fields: ["unitTime"],
                            GTE: moment(moment(startDate).format("YYYY-MM-DD") + " 00:00:00").utc().format("YYYY-MM-DDTHH:mm:ss.sss") + "Z",
                            LTE: moment(moment(endDate).format("YYYY-MM-DD") + " 23:59:00").utc().format("YYYY-MM-DDTHH:mm:ss.sss") + "Z"
                        },
                        conditionOperator: "AND"
                    });
                } else {
                    newConditions.push({
                        range: {
                            fields: ["unitTime"],
                            GTE: moment(moment(startDate).format("YYYY-MM-DD") + " 00:00:00").utc().format("YYYY-MM-DDTHH:mm:ss.sss") + "Z",
                            LTE: moment(moment(endDate).format("YYYY-MM-DD") + " 23:59:00").utc().format("YYYY-MM-DDTHH:mm:ss.sss") + "Z"
                        },
                        conditionOperator: "AND"
                    });
                }
                tableRef?.current?.getData({ newRows: type === 'last' ? parseInt(last.id, 10) : undefined, newConditions });
            } else {
                NotificationManager.warning(
                    messages['maintenance_warnings_tab_date_range'],
                    messages['maintenance_warnings_tab_title'],
                    5000,
                );
            }
        }
        return () => {
            setInitialLoad(true);
        }
    }, [startDate, endDate])

    return (
        <div className="troubleshooting-warnings-tab">
            <Container
                maxWidth={false}
                className={cx(!tabMode ? 'container-view' : 'container-view-tab')}
            >
                <Paper elevation={0}>
                    <Grid
                        container
                        direction="row"
                        justifyContent="flex-start"
                        alignItems="center"
                        className={tabMode ? "maintenance-warnings-events-event" : "maintenance-warnings-events-main"}
                    >
                        {
                            !tabMode &&
                            <>
                                <Grid item xs={6}>
                                    <Typography variant="h5" gutterBottom component="div">
                                        {messages['maintenancewarningevent_main_title']} {/* ({total}) */}
                                    </Typography>
                                </Grid>
                                <Grid item xs={6} className="main-view-buttons">
                                </Grid>
                                <div style={{ width: '100%', marginBottom: 10, marginTop: 5 }}>
                                    <Divider style={{ backgroundColor: '#999999' }} />
                                </div>
                            </>
                        }
                        <DataViewInfiniteScroll
                            ref={tableRef}
                            columns={companyId ? companyColumns : device?.id ? deviceColumns : mainColumns}
                            name={companyId ? `maintenance-warnings-events-company-tab-v10` : device?.id ? `maintenance-warnings-events-device-tab-v10` : "maintenance-warnings-events-main-v10"}
                            entity={companyId ? "maintenance-warning-events-company" : device?.id ? "maintenance-warning-events-device" : "maintenance-warning-events-main"}
                            showTotalCount={type !== 'last'}
                            infiniteScroll={type !== 'last'}
                            onChangeColumns={handleChangeColumns}
                            onChangeConditions={handleChangeConditions}
                            extraButton={renderExtraButton}
                            tableButtons={["export", 'orderColumn']}
                            onChangeTotal={(total) => setTotal(total)}
                            onFilters={() => { setOpenPanel(!openPanel) }}
                            graphqlConfig={{
                                function: "searchMaintenanceWarningEvents",
                                sortType: "MaintenanceWarningEventSortInput",
                                conditionsType: "MaintenanceWarningEventSearchInput",
                                customQuery: "id unitId unit { id label esn timezone linkedMode linkedUnitId linkedUnit { id esn label } deviceModel { manufacturerId manufacturer { id name } } company { id name } } details",
                                dynamicQuery: true,
                            }}
                            searchConfig={{
                                barWidth: 60,
                                optionValue: "id",
                                optionLabel: "name",
                                noOptions: true,
                            }}
                            uiConfig={{
                                hideSplit: true,
                                topHeight: 200,
                                height: tabMode ? 500 : height ? height : 500,
                                filterSelected: (account || deviceModel || status || manufacturer),
                                hidePagination: type === "last",
                                buttonsFlexWrap: false,
                            }}
                            exportConfig={{
                                fileName: companyId ? "maintenancewarningseventscompany" : device?.id ? "maintenancewarningseventsdevice" : "maintenancewarningseventsmain",
                            }}
                            defaultsConfig={{
                                sort: { field: "unitTime", order: "DESC" },
                            }}
                            selectionConfig={{
                                showCounter: false,
                                disabled: true
                            }}
                        />
                    </Grid>
                </Paper>
                {openModal && <Details />}
                {
                    !tabMode && !openModal &&
                    <AdvancedFilters
                        setOpenPanel={setOpenPanel}
                        openPanel={openPanel}
                        tableRef={tableRef}
                        manufacturer={manufacturer}
                        setManufacturer={setManufacturer}
                        deviceModel={deviceModel}
                        setDeviceModel={setDeviceModel}
                        status={status}
                        setStatus={setStatus}
                        account={account}
                        setAccount={setAccount}
                    />
                }
            </Container>
        </div>
    );
}

export default MaintenanceWarningEvents;
